import {
  Button,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Select,
  Typography,
} from "antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { CalendarOutlined, LockOutlined } from "@ant-design/icons";

import { translation } from "../../../i18n/translationConstant";
import CommonModal from "../../Common/Components/CommonModal";
import AddIdentifierImage from "../../../Assets/add_identifier.png";
import OTPInputComponent from "../../Common/Components/OTPModal";
import moment from "moment";

import "../index.css";

import {
  IdentifierCategory,
  IdentifierType,
  StrongIdentifier,
} from "../../../Utils/Constants/identifierType";

import {
  validAadhar,
  validAccNo,
  validEmail,
  validMobile,
  validPan,
} from "../../../Utils/Constants/identifierValidator";

import { EntityIdentifier } from "../../../Models/Request/Identifier/EntityIdentifier";

interface IAddUserIdentificationProps extends React.PropsWithChildren {
  visible: boolean;
  handleCancel: () => void;
  identifierEntered: (otp: any) => void;
  isAddIdentifierVerification: boolean;
  onOTPSubmit: (otp: string, identifierNumber: any) => void;
  label: string;
  form?: FormInstance<any>;
  multipleIdentifiers: boolean;
  mobileNumber?: string;
  isForgotPassword: boolean;
  entityIdentifiers: EntityIdentifier[];
}

const UserIdentification: React.FC<IAddUserIdentificationProps> = ({
  visible,
  handleCancel,
  identifierEntered,
  isAddIdentifierVerification,
  onOTPSubmit,
  form,
  mobileNumber,
  isForgotPassword,
  entityIdentifiers,
}) => {
  const [t] = useTranslation();

  const loading: boolean = useSelector(
    (state: any) => state.loader.searchLoader
  );

  const [identifierCategory, setIdentifierCategory] =
    useState<IdentifierCategory>(IdentifierCategory.STRONG);

  const [selectedIdentifierType, setSelectedIdentifierType] =
    useState<string>("MOBILE");

  const [identifierObject, setIdentifierObject] = useState({
    categoryType: "",
    type: "",
    value: "",
  });

  /*
   * -----------------------------------------
   * OTP
   * -----------------------------------------
   */

  const onOTPValueSubmit = (otp: string) => {
    onOTPSubmit(
      otp,
      isForgotPassword ? identifierObject.value : identifierObject
    );
  };

  /*
   * -----------------------------------------
   * CANCEL
   * -----------------------------------------
   */

  const onCancel = () => {
    handleCancel();

    setIdentifierObject({
      categoryType: "",
      type: "",
      value: "",
    });

    setSelectedIdentifierType("");

    form?.resetFields();
  };

  /*
   * -----------------------------------------
   * RESEND OTP
   * -----------------------------------------
   */

  const onIdentifierEntered = () => {
    identifierEntered(form?.getFieldValue("value"));
  };

  /*
   * -----------------------------------------
   * IDENTIFIER TYPE CHANGE
   * -----------------------------------------
   */

  const onIdentifierTypeChange = (value: any) => {
    setSelectedIdentifierType(value);

    const selectedItem = entityIdentifiers.find(
      (item: EntityIdentifier) => item.type === value
    );

    const category = selectedItem?.category?.toUpperCase() as IdentifierCategory;

    setIdentifierCategory(category);

    form?.resetFields(["value"]);
  };

  /*
   * -----------------------------------------
   * VALIDATION
   * -----------------------------------------
   */

  const customValidator = (_: any, value: any) => {
    if (!value) {
      return Promise.resolve();
    }

    switch (selectedIdentifierType) {
      case StrongIdentifier.MOBILE: {
        if (!validMobile.test(value)) {
          return Promise.reject(
            new Error(t(translation.errorInvalidPhoneNumber))
          );
        }

        break;
      }

      case IdentifierType.AADHAR: {
        if (!validAadhar.test(value)) {
          return Promise.reject(
            new Error(t(translation.errorEnterValidAadhaar))
          );
        }

        break;
      }

      case IdentifierType.PAN: {
        if (!validPan.test(value)) {
          return Promise.reject(
            new Error(t(translation.errorEnterValidPan))
          );
        }

        break;
      }

      case IdentifierType.EMAIL: {
        if (!validEmail.test(value)) {
          return Promise.reject(
            new Error(t(translation.errorEnterValidEmail))
          );
        }

        break;
      }

      case IdentifierType.ACCNO: {
        if (!validAccNo.test(value)) {
          return Promise.reject(
            new Error(t(translation.errorEnterValidAccountNumber))
          );
        }

        break;
      }

      case IdentifierType.DOB: {
        /*
         * Ant Design v4 DatePicker uses Moment.
         * Do not treat the value as a string here.
         */
        if (!moment.isMoment(value) || !value.isValid()) {
          return Promise.reject(
            new Error(t(translation.errorInvalidDate))
          );
        }

        /*
         * Future DOB is not allowed.
         */
        if (value.isAfter(moment(), "day")) {
          return Promise.reject(
            new Error(t(translation.errorInvalidDate))
          );
        }

        break;
      }

      default:
        break;
    }

    return Promise.resolve();
  };

  /*
   * -----------------------------------------
   * DOB
   * -----------------------------------------
   */

  const disabledDate = (current: any) => {
    return current && current.valueOf() > Date.now();
  };

  /*
   * -----------------------------------------
   * FORM SUBMIT
   * -----------------------------------------
   */

  const onFormFinish = (values: any) => {
    const data = {
      categoryType: identifierCategory,
      value:
      selectedIdentifierType === IdentifierType.PAN
        ? values["value"]?.toUpperCase()
        :selectedIdentifierType === IdentifierType.DOB &&
        values["value"]
          ? values["value"].format("DD/MM/YYYY")
          : values["value"],
      type: values["type"],
    };

    identifierEntered(data);
    setIdentifierObject(data);
  };

  /*
   * -----------------------------------------
   * OTP SCREEN
   * -----------------------------------------
   */

  if (isAddIdentifierVerification) {
    return (
      <OTPInputComponent
        title={t(translation.lblVerify)}
        isOpen={visible}
        onOtpSubmit={onOTPValueSubmit}
        onCancel={onCancel}
        okText={t(translation.lblConfirm)}
        onResend={onIdentifierEntered}
        mobileNumber={
          isForgotPassword
            ? mobileNumber
            : form?.getFieldValue("value")
        }
      />
    );
  }

  /*
   * -----------------------------------------
   * ADD IDENTIFIER POPUP
   * -----------------------------------------
   */

  return (
    <CommonModal visible={visible} onCancel={onCancel}>
      <div className="identifierModal">

        {/* HEADER */}
        <div className="identifierModalHeader">

          <div className="identifierModalImage">
            <img
              src={AddIdentifierImage}
              alt={t("lblAddIdentifier")}
            />
          </div>

          <div className="identifierModalHeaderText">
            <Typography.Text className="identifierModalTitle">
              {isForgotPassword
                ? t("lblForgotPassword")
                : t("lblAddIdentifier")}
            </Typography.Text>

            {!isForgotPassword && (
              <Typography.Text className="identifierModalSubtitle">
                {t("lblIdentifierSubtitle")}
              </Typography.Text>
            )}
          </div>

        </div>

        {/* FORM */}
        <Form
          form={form}
          className="identifierModalForm"
          onFinish={onFormFinish}
        >

          {/* IDENTIFIER TYPE */}
          {!isForgotPassword && (
            <div className="identifierField">

              <Typography.Text className="identifierFieldLabel">
                {t(translation.lblIdentifierType)}
              </Typography.Text>

              <Form.Item
                name="type"
                rules={[
                  {
                    required: true,
                    message: '{t("lblPleaseSelectIdentifierType")}',
                  },
                ]}
              >
                <Select
                  placeholder={t("lblSelectIdentifierType")}
                  className="identifierTypeSelector"
                  onChange={onIdentifierTypeChange}
                  dropdownClassName="identifierDropdown"
                >
                  {entityIdentifiers
                    .filter(
                      (identifier: EntityIdentifier) =>
                        identifier.type !== IdentifierType.MOBILE
                    )
                    .map(
                      (
                        identifier: EntityIdentifier,
                        index: number
                      ) => (
                        <Select.Option
                          key={`${identifier.type}-${index}`}
                          value={identifier.type}
                        >
                          {identifier.type}
                        </Select.Option>
                      )
                    )}

                  {/* {entityIdentifiers.length > 0 &&
                    entityIdentifiers.map(
                      (identifier: EntityIdentifier, index: number) => (
                        <Select.Option
                          key={`${identifier.type}-${index}`}
                          value={identifier.type}
                        >
                          {identifier.type}
                        </Select.Option>
                      )
                    )} */}
                </Select>
              </Form.Item>

            </div>
          )}

          {/* IDENTIFIER VALUE */}
          <div className="identifierField">

            <Typography.Text className="identifierFieldLabel">
              {isForgotPassword
                ? t(translation.lblMobileNumber)
                : t(translation.lblIdentifierValue)}
            </Typography.Text>

            <Form.Item
              name="value"
              rules={[
                {
                  required: true,
                  message: t(translation.errorRequiredvalue),
                },
                {
                  validator: customValidator,
                },
              ]}
            >
              {selectedIdentifierType === IdentifierType.DOB ? (
                <DatePicker
                  className="identifierDatePicker"
                  placeholder="DD/MM/YYYY"
                  disabledDate={disabledDate}
                  popupClassName="dobPicker"
                  format="DD/MM/YYYY"
                  showToday={false}
                  suffixIcon={<CalendarOutlined />}
                />
              ) : (
                <Input
                  className="identifierInput"
                  maxLength={isForgotPassword ? 10 : undefined}
                  placeholder={
                    isForgotPassword
                      ? t(translation.lblMobileNumber)
                      : t(translation.lblEnterIdentifierValue)
                  }

                  onChange={(event) => {
                    if (selectedIdentifierType === IdentifierType.PAN) {
                      const value = event.target.value.toUpperCase();

                      form?.setFieldsValue({
                        value,
                      });
                    }
                 }}
                />
              )}
            </Form.Item>

          </div>

          {/* SECURITY INFORMATION */}
          {!isForgotPassword && (
            <div className="identifierSecurityBox">

              <div className="identifierSecurityIcon">
                <LockOutlined />
              </div>

              <div className="identifierSecurityText">
                <Typography.Text className="identifierSecurityTitle">
                  {t("lblInformationSecure")}
                </Typography.Text>

                <Typography.Text className="identifierSecurityDescription">
                  {t("lblUsedToFindAccounts")}
                </Typography.Text>
              </div>

            </div>
          )}

          {/* ACTION BUTTONS */}
          <div className="identifierModalActions">

            <Button
              htmlType="button"
              className="identifierCancelButton"
              onClick={onCancel}
            >
              {t(translation.lblCancel)}
            </Button>

            <Button
              htmlType="submit"
              type="primary"
              loading={loading}
              className="identifierConfirmButton"
            >
              {t(translation.lblConfirm)}
            </Button>

          </div>

        </Form>

      </div>
    </CommonModal>
  );
};

export default UserIdentification;